/**
 * Remembering an import so it can be picked up again.
 *
 * A phone that locks its screen suspends the tab and the upload dies with it.
 * Before this, a 3.5 GB archive that failed at ninety percent started again
 * from nothing — which on a phone means it may never finish at all.
 *
 * Only the job identity is kept here. What has actually arrived is asked of
 * the server, because the client is the party that just crashed and its idea
 * of what it finished is exactly what cannot be trusted.
 */

const KEY = 'wa.import.job';

export interface SavedJob {
  /** Enough to recognise the same file again without reading it. */
  fileKey: string;
  importId: string;
  startedAt: number;
  /** How many times this job has been picked up. Counted here rather than in
   *  memory because the loop it guards against is one reload per attempt: a
   *  tab that starts fresh has forgotten everything except this. */
  attempts?: number;
}

/** Two. The first retry is a recovery; by the third the evidence is that this
 *  import cannot finish, and resuming it again only postpones a clean start. */
const MAX_ATTEMPTS = 2;

/** Name, size and mtime. Not proof of identity, but wrong matches are cheap:
 *  the server re-checks every pack against what it actually holds. */
export const fileKeyOf = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;

export function rememberJob(file: File, importId: string) {
  try {
    localStorage.setItem(KEY, JSON.stringify({
      fileKey: fileKeyOf(file), importId, startedAt: Date.now(),
    } satisfies SavedJob));
  } catch { /* private mode; resume is a convenience, not a requirement */ }
}

/**
 * The job to pick up, if picking it up is still a reasonable thing to do.
 *
 * Calling this counts as an attempt, which is the point: an import that stalls
 * without failing leaves no error to clear the job, so every reload resumed the
 * same dead import for ever. Someone watching that sees nothing happen, twice,
 * and then a third time.
 */
export function savedJobFor(file: File): SavedJob | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const job = JSON.parse(raw) as SavedJob;
    if (job.fileKey !== fileKeyOf(file)) return null;
    // Staging objects are swept after seven days, so an older job has nothing
    // left to resume into.
    if (Date.now() - job.startedAt > 6 * 24 * 60 * 60 * 1000) { forgetJob(); return null; }

    const attempts = (job.attempts ?? 0) + 1;
    if (attempts > MAX_ATTEMPTS) { forgetJob(); return null; }
    localStorage.setItem(KEY, JSON.stringify({ ...job, attempts } satisfies SavedJob));
    return job;
  } catch {
    return null;
  }
}

export function forgetJob() {
  try { localStorage.removeItem(KEY); } catch { /* nothing to do */ }
}
